import { createApp, markRaw, type App } from 'vue';

import DataTablesLengthSelect from '@/modules/shipments/components/DataTablesLengthSelect.vue';
import type { SelectOption } from '@/components/ui/select/Select';

type EnhancedLengthSelect = {
  app: App;
  mountHost: HTMLDivElement;
  select: HTMLSelectElement;
};

const enhancedSelects = new WeakMap<HTMLSelectElement, EnhancedLengthSelect>();

function getSelectOptions(select: HTMLSelectElement): SelectOption[] {
  return Array.from(select.options).map((option) => ({
    value: option.value,
    label: option.text
  }));
}

export function enhanceDataTablesLengthSelect(select: HTMLSelectElement): void {
  if (select.dataset.uiSelectEnhanced === 'true' || enhancedSelects.has(select)) {
    return;
  }

  const label = select.closest('label');
  const mountHost = document.createElement('div');
  mountHost.className = 'ui-select-mount';
  select.insertAdjacentElement('beforebegin', mountHost);
  select.classList.add('ui-select-native-hidden');
  select.dataset.uiSelectEnhanced = 'true';
  label?.classList.add('has-ui-select');

  const app = createApp(DataTablesLengthSelect, {
    select: markRaw(select),
    options: getSelectOptions(select)
  });

  app.mount(mountHost);
  enhancedSelects.set(select, { app, mountHost, select });
}

export function enhanceDataTablesLengthSelects(root: ParentNode = document): void {
  root.querySelectorAll<HTMLSelectElement>('.dataTables_length select').forEach((select) => {
    enhanceDataTablesLengthSelect(select);
  });
}

export function destroyDataTablesLengthSelect(select: HTMLSelectElement): void {
  const enhanced = enhancedSelects.get(select);
  if (!enhanced) {
    return;
  }

  enhanced.app.unmount();
  enhanced.mountHost.remove();
  enhanced.select.classList.remove('ui-select-native-hidden');
  delete enhanced.select.dataset.uiSelectEnhanced;
  enhanced.select.closest('label')?.classList.remove('has-ui-select');
  enhancedSelects.delete(select);
}
